import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  interpolate,
} from "remotion";
import { loadFont as loadDev } from "@remotion/google-fonts/NotoSansDevanagari";
import data from "./chakka_lyrics_data.json";

const { fontFamily: devFont } = loadDev("normal", {
  weights: ["500", "700"],
  subsets: ["devanagari", "latin"],
});

// ── canvas ───────────────────────────────────────────────────────────────
const W = 1280;
const H = 704;

// ── lyric roller geometry — ported from KamakhyaPlayer, scaled 0.7 (30% smaller) ──
// Kamakhya: LINE_GAP 70, ROLLER_H 280, ROLLER_W 600, ANCHOR_Y 150, font 35/33.
const LINE_GAP = 49; // 70 * 0.7
const ROLLER_H = 196; // 280 * 0.7
const ROLLER_W = 420; // 600 * 0.7
const ANCHOR_Y = 105; // 150 * 0.7  (current line's y inside the box)
const FONT_CUR = 24.5; // 35 * 0.7
const FONT_OTH = 23.1; // 33 * 0.7
const SHIFT_DUR = 0.4; // seconds the line-roll takes, ENDING at the next line's first word

// bottom-right placement on full-bleed footage (no player card here)
const BOX_RIGHT = 30; // box right edge, px from canvas right
const BOX_BOTTOM = 30; // box bottom, px from canvas bottom

type Line = { text: string; start: number; end: number };
const lines: Line[] = data as Line[];

const VOCAL_ONSET = lines[0].start; // first sung line (18.0s) — fade roller in here
const GAP_FADE = 0.6; // s to fade out/in around an instrumental gap

export const ChakkaLyrics: React.FC = () => {
  const frame = useCurrentFrame();
  const { fps, durationInFrames } = useVideoConfig();
  const t = frame / fps;

  // ── tail fade-out: the whole overlay (title + roller) fades over the LAST 7s,
  //    matching the video's 7s freeze-frame fade-out outro. Title persists the
  //    full duration otherwise (start→end). ──
  const TAIL = 7;
  const tailFade = interpolate(
    frame,
    [durationInFrames - TAIL * fps, durationInFrames - 1],
    [1, 0],
    { extrapolateLeft: "clamp", extrapolateRight: "clamp" }
  );

  // current fractional lyric position
  let cur = 0;
  for (let i = 0; i < lines.length; i++) {
    if (t >= lines[i].start) cur = i;
    else break;
  }
  // HOLD on the current line while sung, then roll up only in the final SHIFT_DUR
  // before the NEXT line's first word starts.
  const nextStart = cur + 1 < lines.length ? lines[cur + 1].start : lines[cur].end + 99;
  const shiftStart = Math.max(lines[cur].start, nextStart - SHIFT_DUR);
  let frac = 0;
  if (t > shiftStart && nextStart > shiftStart) frac = Math.min(1, (t - shiftStart) / (nextStart - shiftStart));
  const eased = frac * frac * (3 - 2 * frac); // smoothstep
  const pos = cur + eased;

  // ── roller visibility: hidden before the first sung line, faded out in gaps ──
  const curEnd = lines[cur].end;
  const nextS2 = cur + 1 < lines.length ? lines[cur + 1].start : curEnd;
  let rollerOp = 1;
  if (t < VOCAL_ONSET) {
    rollerOp = interpolate(t, [VOCAL_ONSET - 0.4, VOCAL_ONSET], [0, 1], {
      extrapolateLeft: "clamp",
      extrapolateRight: "clamp",
    });
  } else if (t > curEnd && nextS2 - curEnd > 1.4) {
    // genuine instrumental gap → fade out after the line ends, fade back in before next
    const outDone = curEnd + GAP_FADE;
    const inStart = nextS2 - GAP_FADE;
    if (t <= outDone) rollerOp = interpolate(t, [curEnd, outDone], [1, 0], { extrapolateRight: "clamp" });
    else if (t >= inStart) rollerOp = interpolate(t, [inStart, nextS2], [0, 1], { extrapolateLeft: "clamp" });
    else rollerOp = 0;
  }

  return (
    <AbsoluteFill style={{ opacity: tailFade }}>
      {/* ── track title — top-left, persistent — Kamakhya "Now Playing:" style ── */}
      <div
        style={{
          position: "absolute",
          top: 24,
          left: 26,
          fontFamily: devFont,
          fontSize: 22,
          color: "#fff",
          letterSpacing: 0.4,
          whiteSpace: "nowrap",
          textShadow: "0 2px 10px rgba(0,0,0,0.9), 0 0 2px rgba(0,0,0,0.6)",
          pointerEvents: "none",
        }}
      >
        <span style={{ fontWeight: 400, opacity: 0.85 }}>Now Playing: </span>
        <span style={{ fontWeight: 700 }}>Jagannath Chaka Nayana</span>
      </div>

      {/* ── lyric roller — 3 lines, right-aligned, bottom-right ── */}
      <div
        style={{
          position: "absolute",
          right: BOX_RIGHT,
          bottom: BOX_BOTTOM,
          width: ROLLER_W,
          height: ROLLER_H,
          perspective: 640,
          WebkitMaskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 28%, rgba(0,0,0,1) 74%, transparent 100%)",
          maskImage:
            "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,1) 28%, rgba(0,0,0,1) 74%, transparent 100%)",
          opacity: rollerOp,
          pointerEvents: "none",
        }}
      >
        {lines.map((ln, i) => {
          const rel = i - pos; // 0 = current line
          if (rel < -1.6 || rel > 1.6) return null;
          const y = ANCHOR_Y + rel * LINE_GAP;
          const ar = Math.abs(rel);
          const opacity = interpolate(ar, [0, 1, 1.6], [1, 0.42, 0], {
            extrapolateLeft: "clamp",
            extrapolateRight: "clamp",
          });
          const isCur = ar < 0.5;
          const scale = interpolate(ar, [0, 1], [1, 0.9], { extrapolateRight: "clamp" });
          const rotX = interpolate(rel, [-1.6, 0, 1.6], [26, 0, -24]); // drum curve
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                right: 0,
                top: y,
                maxWidth: ROLLER_W,
                textAlign: "right",
                transform: `translateY(-50%) rotateX(${rotX}deg) scale(${scale})`,
                transformOrigin: "right center",
                opacity,
              }}
            >
              <span
                style={{
                  fontFamily: devFont,
                  fontWeight: isCur ? 700 : 500,
                  fontSize: isCur ? FONT_CUR : FONT_OTH,
                  color: "#fff",
                  letterSpacing: 0.3,
                  textShadow: isCur
                    ? "0 2px 18px rgba(0,0,0,0.9), 0 0 26px rgba(255,210,120,0.4)"
                    : "0 2px 12px rgba(0,0,0,0.85)",
                }}
              >
                {ln.text}
              </span>
            </div>
          );
        })}
      </div>
    </AbsoluteFill>
  );
};

;
    var _remotion_globalVariableA;
    var _remotion_globalVariableB;
    // Legacy CSS implementations will `eval` browser code in a Node.js context
    // to extract CSS. For backwards compatibility, we need to check we're in a
    // browser context before continuing.
    if (typeof self !== 'undefined' &&
        // AMP / No-JS mode does not inject these helpers:
        '$RefreshHelpers$' in self) {
        const currentExports = __webpack_module__.exports;
        const prevExports = (_remotion_globalVariableB = (_remotion_globalVariableA = __webpack_module__.hot.data) === null || _remotion_globalVariableA === void 0 ? void 0 : _remotion_globalVariableA.prevExports) !== null && _remotion_globalVariableB !== void 0 ? _remotion_globalVariableB : null;
        // This cannot happen in MainTemplate because the exports mismatch between
        // templating and execution.
        self.$RefreshHelpers$.registerExportsForReactRefresh(currentExports, __webpack_module__.id);
        // A module can be accepted automatically based on its exports, e.g. when
        // it is a Refresh Boundary.
        if (self.$RefreshHelpers$.isReactRefreshBoundary(currentExports)) {
            // Save the previous exports on update so we can compare the boundary
            // signatures.
            __webpack_module__.hot.dispose((data) => {
                data.prevExports = currentExports;
            });
            // Unconditionally accept an update to this module, we'll check if it's
            // still a Refresh Boundary later.
            __webpack_module__.hot.accept();
            // This field is set when the previous version of this module was a
            // Refresh Boundary, letting us know we need to check for invalidation or
            // enqueue an update.
            if (prevExports !== null) {
                // A boundary can become ineligible if its exports are incompatible
                // with the previous exports.
                //
                // For example, if you add/remove/change exports, we'll want to
                // re-execute the importing modules, and force those components to
                // re-render. Similarly, if you convert a class component to a
                // function, we want to invalidate the boundary.
                if (self.$RefreshHelpers$.shouldInvalidateReactRefreshBoundary(prevExports, currentExports)) {
                    __webpack_module__.hot.invalidate();
                }
                else {
                    self.$RefreshHelpers$.scheduleUpdate();
                }
            }
        }
        else {
            // Since we just executed the code for the module, it's possible that the
            // new exports made it ineligible for being a boundary.
            // We only care about the case when we were _previously_ a boundary,
            // because we already accepted this update (accidental side effect).
            const isNoLongerABoundary = prevExports !== null;
            if (isNoLongerABoundary) {
                __webpack_module__.hot.invalidate();
            }
        }
    }
